import { useCallback, useRef, useState } from 'react';
import html2canvas from 'html2canvas';

export type ScreenRecordingResult = {
  blob: Blob;
  url: string;
};

type StartOptions = {
  videoBitsPerSecond?: number;
  includeAudio?: boolean;
  // When provided, attempt to crop the captured tab to this element (if supported)
  cropElement?: HTMLElement | null;
  // Frames per second used by canvas fallback (iOS Safari)
  fps?: number;
};

export const useScreenRecorder = () => {
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const objectUrlRef = useRef<string | null>(null);
  const recorderMimeTypeRef = useRef<string>('video/webm');
  const recordedBytesRef = useRef<number>(0);
  const noDataWatchdogRef = useRef<number | null>(null);
  const fallbackTriedRef = useRef<boolean>(false);
  const canvasDrawTimerRef = useRef<number | null>(null);
  const canvasElementRef = useRef<HTMLCanvasElement | null>(null);

  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const start = useCallback(async (options: StartOptions = {}): Promise<void> => {
    if (isRecording) return;
    setError(null);
    try {
      // Ensure this tab is focused before prompting capture
      const ensureTabFocus = async () => {
        try { window.focus(); } catch (_) {}
        if (document.visibilityState === 'visible') return;
        await new Promise<void>((resolve) => {
          const onVisible = () => {
            if (document.visibilityState === 'visible') {
              cleanup();
              resolve();
            }
          };
          const cleanup = () => {
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('focus', onVisible);
          };
          document.addEventListener('visibilitychange', onVisible);
          window.addEventListener('focus', onVisible);
          // Safety timeout to avoid hanging forever
          setTimeout(() => { cleanup(); resolve(); }, 1500);
        });
      };

      await ensureTabFocus();

      const isIOSorSafari = (() => {
        try {
          const ua = navigator.userAgent || '';
          const isIOS = /iPad|iPhone|iPod/i.test(ua) || (ua.includes('Mac') && typeof (window as any).ontouchend !== 'undefined');
          const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
          return isIOS || isSafari;
        } catch (_) { return false; }
      })();

      // Avoid microphone permission prompts on iOS/Safari by disabling audio capture
      const wantAudio: boolean = options.includeAudio === true && !isIOSorSafari;

      // iOS/Safari에서는 사용자 제스처 타이밍 제약과 호환성 이슈가 많아 캔버스 폴백을 선호
      const hasDisplayCapture = !!(navigator.mediaDevices && (navigator.mediaDevices as any).getDisplayMedia);
      const shouldUseCanvasFallback = (isIOSorSafari && !!options.cropElement) || (!hasDisplayCapture && !!options.cropElement);

      let stream: MediaStream | null = null;
      if (!shouldUseCanvasFallback && navigator.mediaDevices && (navigator.mediaDevices as any).getDisplayMedia) {
        // Prefer capturing the current browser tab only (Chromium non-standard hints must be top-level)
       const constraints: any = {
          video: true,
          audio: wantAudio,
          // Chromium-specific display-capture hints
          preferCurrentTab: true,
          selfBrowserSurface: 'include',
          surfaceSwitching: 'exclude',
          monitorTypeSurfaces: 'exclude',
          systemAudio: 'exclude',
        };
        try {
          stream = await (navigator.mediaDevices as any).getDisplayMedia(constraints);
        } catch (_) {
          // Fallback to basic constraints if some browsers reject non-standard keys
          stream = await navigator.mediaDevices.getDisplayMedia({
            video: true,
            audio: wantAudio,
          });
        }
      }

      const isDisplayCapture = !!stream;

      // Fallback: canvas-based capture of a specific DOM element
      if (!stream) {
        const targetElement = options.cropElement || document.body;
        if (!targetElement) throw new Error('No element available for canvas capture');

        // Prepare a stable canvas to be captured
        const rect = targetElement.getBoundingClientRect();
        const dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1));
        const fps = Math.max(5, Math.min(30, options.fps || 15));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.floor(rect.width * dpr));
        canvas.height = Math.max(1, Math.floor(rect.height * dpr));
        canvas.style.width = `${Math.max(1, Math.floor(rect.width))}px`;
        canvas.style.height = `${Math.max(1, Math.floor(rect.height))}px`;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Failed to get 2D context for canvas capture');

        // Draw loop using html2canvas at limited FPS
        const drawFrame = async () => {
          try {
            const snap = await html2canvas(targetElement as HTMLElement, {
              backgroundColor: '#000',
              logging: false,
              scale: dpr,
              useCORS: true,
              allowTaint: true,
              windowWidth: document.documentElement.clientWidth,
              windowHeight: document.documentElement.clientHeight,
            });
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(snap, 0, 0, canvas.width, canvas.height);
          } catch (_) { /* ignore */ }
        };
        // Kick off immediately
        await drawFrame();
        // Then schedule repeated snapshots
        const intervalMs = Math.floor(1000 / fps);
        if (canvasDrawTimerRef.current) window.clearInterval(canvasDrawTimerRef.current);
        canvasDrawTimerRef.current = window.setInterval(drawFrame, intervalMs);

        canvasElementRef.current = canvas;
        // Produce a stream from the canvas
        const canvasStream = (canvas as any).captureStream ? (canvas as any).captureStream(fps) : (canvas as any).mozCaptureStream ? (canvas as any).mozCaptureStream(fps) : null;
        if (!canvasStream) throw new Error('Canvas captureStream is not supported');

        // Optionally add microphone audio if requested (disabled for iOS/Safari)
        if (wantAudio) {
          try {
            const audio = await navigator.mediaDevices.getUserMedia({ audio: true });
            audio.getAudioTracks().forEach((t) => canvasStream.addTrack(t));
          } catch (_) { /* ignore audio if denied */ }
        }

        stream = canvasStream as MediaStream;
      }

      if (!stream) {
        throw new Error('Failed to acquire capture stream');
      }

      const captureStream = stream as MediaStream;

      // Ensure we only capture the browser tab (exclude window/screen toolbars)
      const [videoTrack] = captureStream.getVideoTracks();
      const settings = (videoTrack?.getSettings?.() || {}) as any;
      const displaySurface = settings.displaySurface as string | undefined;
      if (displaySurface && displaySurface !== 'browser') {
        // Prefer tab, but do not abort recording – continue with best effort
        // console.warn('Display surface is not a browser tab. Proceeding with best-effort capture.');
      }

      // Disable experimental cropTo for reliability
      try {
        // no-op (intentionally disabled)
      } catch (_) { /* ignore */ }

      // Hint encoder that we capture motion frames
      try {
        if (videoTrack && 'contentHint' in videoTrack) {
          (videoTrack as any).contentHint = 'motion';
        }
        if (videoTrack && typeof (videoTrack as any).applyConstraints === 'function') {
          await (videoTrack as any).applyConstraints({ frameRate: { ideal: 30, max: 30 } });
        }
      } catch (_) { /* ignore */ }

      // Create MediaRecorder preferring MP4/H.264 on all browsers; WebM only when MP4 recording is unsupported
      let recorder: MediaRecorder;
      const desiredBitrate = options.videoBitsPerSecond || 2_000_000;
      const preferredMimeTypes = [
        'video/mp4;codecs=avc1.42E01E,mp4a.40.2',
        'video/mp4;codecs=avc1,mp4a.40.2',
        'video/mp4;codecs=h264,aac',
        'video/mp4;codecs=avc1',
        'video/mp4;codecs=h264',
        'video/mp4',
        'video/webm;codecs=vp9,opus',
        'video/webm;codecs=vp8,opus',
        'video/webm',
      ];
      let selectedMime: string | undefined;
      for (const mt of preferredMimeTypes) {
        try {
          if ((window as any).MediaRecorder && (MediaRecorder as any).isTypeSupported && (MediaRecorder as any).isTypeSupported(mt)) {
            selectedMime = mt;
            break;
          }
        } catch (_) { /* ignore */ }
      }

      // Chromium MP4 output becomes undecodable after a mid-recording resolution change
      // (e.g. the "sharing this tab" bar resizes the tab), so record tab capture via a fixed-size canvas
      let recordStream: MediaStream = captureStream;
      if (isDisplayCapture && videoTrack && (selectedMime || '').includes('mp4')) {
        try {
          const sourceVideo = document.createElement('video');
          sourceVideo.muted = true;
          sourceVideo.playsInline = true;
          sourceVideo.srcObject = new MediaStream([videoTrack]);
          await sourceVideo.play();
          const outWidth = Math.max(2, Math.floor((settings.width || sourceVideo.videoWidth || 1280) / 2) * 2);
          const outHeight = Math.max(2, Math.floor((settings.height || sourceVideo.videoHeight || 720) / 2) * 2);
          const fixedCanvas = document.createElement('canvas');
          fixedCanvas.width = outWidth;
          fixedCanvas.height = outHeight;
          const fixedCtx = fixedCanvas.getContext('2d');
          const fixedCanvasStream = (fixedCanvas as any).captureStream ? (fixedCanvas as any).captureStream(30) as MediaStream : null;
          if (fixedCtx && fixedCanvasStream) {
            const drawFixedFrame = () => {
              const srcWidth = sourceVideo.videoWidth;
              const srcHeight = sourceVideo.videoHeight;
              if (!srcWidth || !srcHeight) return;
              const scale = Math.min(outWidth / srcWidth, outHeight / srcHeight);
              const drawWidth = srcWidth * scale;
              const drawHeight = srcHeight * scale;
              fixedCtx.fillStyle = '#000';
              fixedCtx.fillRect(0, 0, outWidth, outHeight);
              fixedCtx.drawImage(sourceVideo, (outWidth - drawWidth) / 2, (outHeight - drawHeight) / 2, drawWidth, drawHeight);
            };
            drawFixedFrame();
            if (canvasDrawTimerRef.current) window.clearInterval(canvasDrawTimerRef.current);
            canvasDrawTimerRef.current = window.setInterval(drawFixedFrame, Math.floor(1000 / 30));
            canvasElementRef.current = fixedCanvas;
            recordStream = new MediaStream([...fixedCanvasStream.getVideoTracks(), ...captureStream.getAudioTracks()]);
          }
        } catch (_) {
          recordStream = captureStream;
        }
      }
      const trackOwnerStream = recordStream === captureStream
        ? captureStream
        : new MediaStream([...captureStream.getTracks(), ...recordStream.getVideoTracks()]);

      try {
        recorder = new MediaRecorder(recordStream, {
          mimeType: selectedMime,
          videoBitsPerSecond: desiredBitrate,
        } as MediaRecorderOptions);
      } catch (_) {
        recorder = new MediaRecorder(recordStream);
      }
      try {
        const actual = (recorder as any).mimeType as string | undefined;
        recorderMimeTypeRef.current = actual && actual.length > 0 ? actual : (selectedMime || 'video/webm');
      } catch (_) {
        recorderMimeTypeRef.current = selectedMime || 'video/webm';
      }

      chunksRef.current = [];
      recordedBytesRef.current = 0;

      recorder.ondataavailable = (e: BlobEvent) => {
        if (e.data && e.data.size > 0) {
          chunksRef.current.push(e.data);
          recordedBytesRef.current += e.data.size;
        }
      };

      recorder.onstop = () => {
        // Stop all tracks
        trackOwnerStream.getTracks().forEach((t) => t.stop());
      };

      mediaStreamRef.current = trackOwnerStream;
      mediaRecorderRef.current = recorder;
      // Use a 1s timeslice; some browsers emit chunks only on >=1000ms
      recorder.start(1000);

      // Watchdog: Safari/iOS에서는 dataavailable이 stop 시점에만 올 수 있으므로 건너뜀
      try {
        if (noDataWatchdogRef.current) {
          clearTimeout(noDataWatchdogRef.current);
        }
        const isSafariEnv2 = (() => {
          try { return /^((?!chrome|android).)*safari/i.test(navigator.userAgent || ''); } catch { return false; }
        })();
        if (isSafariEnv2) {
          // Skip watchdog on Safari/iOS
          noDataWatchdogRef.current = null as any;
        } else {
          noDataWatchdogRef.current = window.setTimeout(async () => {
          const picked = recorderMimeTypeRef.current || '';
          if (recordedBytesRef.current > 0 || fallbackTriedRef.current || !picked.includes('mp4')) return;
          // Chromium MP4 muxer emits data only at keyframe fragments; force a flush and wait before falling back
          try { recorder.requestData(); } catch (_) {}
          await new Promise((resolve) => setTimeout(resolve, 2000));
          const isStillActive = mediaRecorderRef.current === recorder && recorder.state === 'recording';
          if (recordedBytesRef.current > 0 || !isStillActive) return;
            fallbackTriedRef.current = true;
            // Detach handlers so stopping the MP4 recorder does not end the shared capture tracks
            recorder.ondataavailable = null;
            recorder.onstop = null;
            try { recorder.stop(); } catch (_) {}
            // Recreate recorder with WEBM explicitly (or re-init from canvas if not already)
            let webmRecorder: MediaRecorder;
            try {
              webmRecorder = new MediaRecorder(recordStream, {
                mimeType: 'video/webm;codecs=vp8,opus',
                videoBitsPerSecond: desiredBitrate,
              });
            } catch (_) {
              webmRecorder = new MediaRecorder(recordStream);
            }
            recorderMimeTypeRef.current = (webmRecorder as any).mimeType || 'video/webm';
            chunksRef.current = [];
            recordedBytesRef.current = 0;
            webmRecorder.ondataavailable = (e: BlobEvent) => {
              if (e.data && e.data.size > 0) {
                chunksRef.current.push(e.data);
                recordedBytesRef.current += e.data.size;
              }
            };
            webmRecorder.onstop = () => {
              trackOwnerStream.getTracks().forEach((t) => t.stop());
            };
            mediaRecorderRef.current = webmRecorder;
            webmRecorder.start(1000);
          }, 2500);
        }
      } catch (_) { /* ignore */ }
      setIsRecording(true);
    } catch (err: any) {
      const message = err?.name || err?.message || 'Failed to start screen recording';
      setError(message);
      throw err;
    }
  }, [isRecording]);

  const stop = useCallback(async (): Promise<ScreenRecordingResult | null> => {
    if (!isRecording || !mediaRecorderRef.current) return null;

    const recorder = mediaRecorderRef.current;

    const stopped: Promise<void> = new Promise((resolve) => {
      recorder.onstop = () => {
        // Ensure tracks are stopped
        if (mediaStreamRef.current) {
          mediaStreamRef.current.getTracks().forEach((t) => t.stop());
        }
        resolve();
      };
    });
    // iOS/Safari failsafe: sometimes 'onstop' never fires. Race with a timeout so UI doesn't hang.
    const stopFailsafe: Promise<void> = new Promise((resolve) => {
      try { setTimeout(resolve, 1200); } catch (_) { resolve(); }
    });
    // Wait for a final dataavailable chunk after stop (Safari sometimes emits it late)
    const finalChunk: Promise<void> = new Promise((resolve) => {
      let settled = false;
      const handler = (e: BlobEvent) => {
        // Push through the microtask queue to ensure chunk is recorded
        settled = true;
        try { setTimeout(resolve, 0); } catch (_) { resolve(); }
      };
      try {
        recorder.addEventListener('dataavailable', handler, { once: true } as any);
      } catch (_) {
        // Fallback timeout if event listener is not supported
        setTimeout(resolve, 300);
        return;
      }
      // If no additional chunk arrives shortly, proceed anyway
      setTimeout(() => { if (!settled) resolve(); }, 500);
    });

    try {
      // Flush any buffered data before stopping to avoid empty blobs
      if (recorder.state === 'recording' && typeof recorder.requestData === 'function') {
        recorder.requestData();
      }
    } catch (_) {
      // ignore
    }
    try { recorder.stop(); } catch (_) { /* ignore */ }
    try { if (noDataWatchdogRef.current) { clearTimeout(noDataWatchdogRef.current); noDataWatchdogRef.current = null; } } catch (_) { /* ignore */ }
    fallbackTriedRef.current = false;
    setIsRecording(false);
    // Proceed even if 'onstop' never arrives (Safari/iOS quirk), but try to wait for the last chunk
    try { await Promise.race([Promise.all([stopped, finalChunk]), stopFailsafe]); } catch (_) { /* ignore */ }
    // Defensive: ensure tracks are stopped in the fallback path
    try {
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((t) => t.stop());
      }
    } catch (_) { /* ignore */ }

    // Stop canvas drawing loop if used
    try {
      if (canvasDrawTimerRef.current) {
        window.clearInterval(canvasDrawTimerRef.current);
        canvasDrawTimerRef.current = null;
      }
      canvasElementRef.current = null;
    } catch (_) { /* ignore */ }

    const selectedMime = recorderMimeTypeRef.current || (mediaRecorderRef.current as any)?.mimeType || 'video/webm';
    // Ensure we captured any data
    const totalSize = recordedBytesRef.current;
    const hasData = totalSize > 0 || chunksRef.current.some((part) => {
      try { return (part as any)?.size > 0; } catch { return true; }
    });
    if (!hasData) {
      return null;
    }
    const blob = new Blob(chunksRef.current, { type: selectedMime });
    chunksRef.current = [];

    // Revoke previous URL if exists
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
    }
    const url = URL.createObjectURL(blob);
    objectUrlRef.current = url;

    try {
      // Expose last recording blob/url within the same document for SPA route usage
      (window as any).__lastRecordingBlob = blob;
      (window as any).__lastRecordingMime = selectedMime;
      (window as any).__lastRecordingObjectUrl = url;
    } catch (_) {
      // ignore
    }

    return { blob, url };
  }, [isRecording]);

  const reset = useCallback(() => {
    // Stop tracks if any
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((t) => t.stop());
      mediaStreamRef.current = null;
    }
    if (mediaRecorderRef.current) {
      try { mediaRecorderRef.current.stop(); } catch (_) { /* ignore */ }
      mediaRecorderRef.current = null;
    }
    if (canvasDrawTimerRef.current) {
      try { window.clearInterval(canvasDrawTimerRef.current); } catch (_) { /* ignore */ }
      canvasDrawTimerRef.current = null;
    }
    canvasElementRef.current = null;
    chunksRef.current = [];
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    setIsRecording(false);
    setError(null);
  }, []);

  return { isRecording, error, start, stop, reset };
};

export default useScreenRecorder;


